import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { QuoteRequest, QuoteType, QuoteStatus, PriceBreakdown, Student } from '@/types';

const QUOTES_COLLECTION = 'quoteRequests';

// Create a new quote request
export async function createQuoteRequest(
  userId: string,
  type: QuoteType,
  data: {
    guardianName: string;
    guardianEmail: string;
    guardianPhone: string;
    pickupAddress: string;
    pickupZipcode: string;
    dropoffAddress: string;
    dropoffZipcode: string;
    students: Student[];
    hubstopId?: string;
    hubstopName?: string;
    selectedSessions?: string[];
    selectedDays?: number;
    semesterType?: 'full' | 'fall' | 'spring' | 'custom';
    lateShuttle?: boolean;
    routeName?: string;
    calculatedPrice?: number;
    priceBreakdown?: PriceBreakdown;
    routeDistance?: number;
    routeDuration?: number;
  }
): Promise<string> {
  const quoteData = {
    userId,
    type,
    status: 'pending' as QuoteStatus,
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, QUOTES_COLLECTION), quoteData);

  // NOTE: Email notification will be sent automatically via Firestore trigger
  // once the Cloud Functions v2 email triggers are deployed
  // The sendQuoteEmail function is triggered automatically when a quote is created

  return docRef.id;
}

// Get a quote by ID
export async function getQuoteById(quoteId: string): Promise<QuoteRequest | null> {
  const docRef = doc(db, QUOTES_COLLECTION, quoteId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
    updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    quotedAt: (data.quotedAt as Timestamp)?.toDate(),
    expiresAt: (data.expiresAt as Timestamp)?.toDate(),
  } as QuoteRequest;
}

// Get all quotes for a user
export async function getUserQuotes(userId: string): Promise<QuoteRequest[]> {
  const q = query(
    collection(db, QUOTES_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      quotedAt: (data.quotedAt as Timestamp)?.toDate(),
      expiresAt: (data.expiresAt as Timestamp)?.toDate(),
    } as QuoteRequest;
  });
}

// Update quote status
export async function updateQuoteStatus(
  quoteId: string,
  status: QuoteStatus
): Promise<void> {
  const docRef = doc(db, QUOTES_COLLECTION, quoteId);
  await updateDoc(docRef, {
    status,
    updatedAt: serverTimestamp(),
    ...(status === 'quoted' && {
      quotedAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days
    }),
  });
}

// Update quote pricing
export async function updateQuotePricing(
  quoteId: string,
  calculatedPrice: number,
  priceBreakdown: PriceBreakdown,
  routeDistance?: number,
  routeDuration?: number
): Promise<void> {
  const docRef = doc(db, QUOTES_COLLECTION, quoteId);
  await updateDoc(docRef, {
    calculatedPrice,
    priceBreakdown,
    routeDistance,
    routeDuration,
    status: 'quoted',
    quotedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
  });
}

// Accept a quote (convert to registration)
export async function acceptQuote(quoteId: string): Promise<string> {
  const quote = await getQuoteById(quoteId);
  if (!quote) {
    throw new Error('Quote not found');
  }

  // Update quote status
  await updateQuoteStatus(quoteId, 'accepted');

  // Create registration
  const registrationData = {
    userId: quote.userId,
    quoteRequestId: quoteId,
    guardianName: quote.guardianName,
    guardianEmail: quote.guardianEmail,
    guardianPhone: quote.guardianPhone,
    pickupAddress: quote.pickupAddress,
    pickupZipcode: quote.pickupZipcode,
    dropoffAddress: quote.dropoffAddress,
    dropoffZipcode: quote.dropoffZipcode,
    routeName: quote.routeName,
    students: quote.students,
    campSessions: quote.selectedSessions,
    status: 'pending',
    emailVerified: false,
    totalAmount: quote.calculatedPrice || 0,
    paymentStatus: 'pending',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const registrationRef = await addDoc(collection(db, 'shuttleRegistrations'), registrationData);

  // NOTE: Confirmation email will be sent automatically via Firestore trigger
  // once the Cloud Functions v2 email triggers are deployed
  // The sendConfirmationEmail function is triggered automatically when a registration is created

  return registrationRef.id;
}

// Calculate hubstop quote price
export function calculateHubstopPrice(
  pricePerWeek: number,
  numberOfStudents: number,
  numberOfWeeks: number,
  lateShuttle: boolean = false
): PriceBreakdown {
  const perStudentPrice = pricePerWeek;
  const sessionPrice = perStudentPrice * numberOfStudents;
  const subtotal = sessionPrice * numberOfWeeks;
  const lateShuttleFee = lateShuttle ? 25 * numberOfWeeks * numberOfStudents : 0;
  const total = subtotal + lateShuttleFee;

  return {
    basePrice: pricePerWeek,
    perStudentPrice,
    numberOfStudents,
    numberOfSessions: numberOfWeeks,
    sessionPrice,
    lateShuttleFee: lateShuttleFee > 0 ? lateShuttleFee : undefined,
    subtotal,
    total,
  };
}

// Calculate yearly quote price
export function calculateYearlyPrice(
  semesterType: 'full' | 'fall' | 'spring' | 'custom',
  customDays: number = 0,
  numberOfStudents: number,
  pricePerDay: number = 35
): PriceBreakdown {
  let numberOfDays: number;
  
  switch (semesterType) {
    case 'full':
      numberOfDays = 180;
      break;
    case 'fall':
    case 'spring':
      numberOfDays = 90;
      break;
    case 'custom':
      numberOfDays = customDays;
      break;
    default:
      numberOfDays = 180;
  }

  const perStudentPrice = pricePerDay * numberOfDays;
  const subtotal = perStudentPrice * numberOfStudents;
  
  // Volume discount
  let discount = 0;
  if (numberOfStudents >= 3) {
    discount = subtotal * 0.1; // 10% discount for 3+ students
  }

  const total = subtotal - discount;

  return {
    basePrice: pricePerDay,
    perStudentPrice,
    numberOfStudents,
    numberOfSessions: numberOfDays,
    sessionPrice: pricePerDay * numberOfStudents,
    subtotal,
    discount: discount > 0 ? discount : undefined,
    total,
  };
}
